import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import {
  Edit3,
  Plus,
  MapPin,
  Bookmark,
  Mail,
  Phone,
  ExternalLink,
  MoreHorizontal,
  Printer,
  Trash2,
  Eye,
  EyeOff,
  Users,
} from 'lucide-react';
import { client } from '@/lib/api';
import {
  formatTimeInZone,
  formatDateInZone,
  instantToZonedWallClock,
  zonedWallClockToInstant,
} from '@/lib/timezone';
import { PERSON_KEY_LABELS, PERSON_KEYS, type PersonKey } from '@everplan/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RichBody, type Mentionable } from './RichBody';
import {
  EditEventDialog,
  StepDialog,
  SaveAsTemplateDialog,
} from '@/components/EventDialogs';

type EventDetail = InferResponseType<(typeof client.api.events)[':id']['$get'], 200>;
type Step = EventDetail['steps'][number];

interface MentionPopoverState {
  anchor: HTMLElement;
  kind: 'vendor' | 'person';
  // For vendor: full project_vendor + vendor + primary contact data
  vendor?: {
    name: string;
    role: string | null;
    vendorId: string;
    location?: string | null;
    contact?: {
      name: string;
      email: string | null;
      phone: string | null;
    };
    phone?: string | null;
  };
  // For person: derived from project's people fields (we only have the key here)
  personKey?: PersonKey;
}

interface EventDocViewProps {
  eventId: string;
  projectId: string;
}

export default function EventDocView({ eventId, projectId }: EventDocViewProps) {
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [participantsDialogStep, setParticipantsDialogStep] = useState<Step | null>(null);
  const [showAddStep, setShowAddStep] = useState(false);
  const [mention, setMention] = useState<MentionPopoverState | null>(null);
  const navigate = useNavigate();

  const { data: event, isLoading, isError } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const res = await client.api.events[':id'].$get({ param: { id: eventId } });
      if (!res.ok) throw new Error('Failed to fetch event');
      return res.json();
    },
    enabled: !!eventId,
  });

  // Mention picker entries — vendors active on the event's project + the four
  // canonical person keys.
  const { data: pvOptions } = useQuery({
    queryKey: ['event', eventId, 'participant-options'],
    queryFn: async () => {
      const res = await client.api.events[':id']['participant-options'].$get({
        param: { id: eventId },
      });
      if (!res.ok) throw new Error('Failed to load vendor options');
      return res.json();
    },
    enabled: !!eventId,
  });
  const mentionables: Mentionable[] = useMemo(() => {
    const fromPeople: Mentionable[] = PERSON_KEYS.map((k) => ({
      id: `person:${k}`,
      kind: 'person',
      label: PERSON_KEY_LABELS[k],
    }));
    const fromVendors: Mentionable[] = (pvOptions ?? []).map(
      (pv: { id: string; vendor: { name: string }; role: string | null }) => ({
        id: `pv:${pv.id}`,
        kind: 'vendor',
        label: pv.vendor.name,
        description: pv.role ?? undefined,
      }),
    );
    return [...fromPeople, ...fromVendors];
  }, [pvOptions]);

  // Build a quick lookup from mention id ('pv:<uuid>') → contact info.
  // The cast works around a Drizzle inferred-type quirk where `columns: { … }`
  // narrows joined relations to `{}` in the response type.
  const vendorByMentionId = useMemo(() => {
    const map = new Map<string, NonNullable<MentionPopoverState['vendor']>>();
    if (!event) return map;
    type LooseVendor = {
      id: string;
      role: string | null;
      vendor: { id: string; name: string; phone: string | null; location: string | null };
      primaryContact: { name: string; email: string | null; phone: string | null } | null;
    };
    for (const step of event.steps) {
      for (const p of step.participants) {
        if (!p.projectVendor) continue;
        const v = p.projectVendor as unknown as LooseVendor;
        map.set(`pv:${v.id}`, {
          name: v.vendor.name,
          role: v.role,
          vendorId: v.vendor.id,
          location: v.vendor.location,
          contact: v.primaryContact
            ? {
                name: v.primaryContact.name,
                email: v.primaryContact.email,
                phone: v.primaryContact.phone,
              }
            : undefined,
          phone: v.vendor.phone,
        });
      }
    }
    return map;
  }, [event]);

  const handleMentionClick = (mentionId: string, kind: 'vendor' | 'person', target?: HTMLElement) => {
    if (!target) return;
    if (kind === 'vendor') {
      const vendor = vendorByMentionId.get(mentionId);
      if (!vendor) return;
      setMention({ anchor: target, kind: 'vendor', vendor });
    } else {
      const personKey = mentionId.replace(/^person:/, '') as PersonKey;
      setMention({ anchor: target, kind: 'person', personKey });
    }
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading event…</p>;
  }
  if (isError || !event) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="py-8 text-center">
          <p className="text-sm text-muted-foreground">Couldn't load event.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="rounded-3xl shadow-ambient overflow-hidden print-page">
        <CardContent className="p-0">
          {/* Header */}
          <div className="px-6 py-4 border-b flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="display-tight text-2xl">{event.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {formatDateInZone(event.startAt, event.timezone, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {' · '}
                {formatTimeInZone(event.startAt, event.timezone)} – {formatTimeInZone(event.endAt, event.timezone)}
                {' · '}
                <span className="text-xs">{event.timezone}</span>
              </p>
              {event.location && (
                <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                  <MapPin className="size-3" />
                  {event.location}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 no-print" data-no-print>
              <Button variant="outline" size="sm" onClick={() => window.print()}>
                <Printer className="size-4" />
                Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSaveAsTemplate(true)}>
                <Bookmark className="size-4" />
                Save as template
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowEditEvent(true)}>
                <Edit3 className="size-4" />
                Edit event
              </Button>
              <Button size="sm" onClick={() => setShowAddStep(true)}>
                <Plus className="size-4" />
                Add step
              </Button>
            </div>
          </div>

          {/* Summary block */}
          <SummaryHeader event={event} />

          {/* Linear timeline */}
          <div>
            {event.steps.length === 0 ? (
              <p className="px-6 py-10 text-center text-sm text-muted-foreground italic">
                No steps yet. Add the first one to start building this day's run-of-show.
              </p>
            ) : (
              event.steps.map((step) => (
                <StepRow
                  key={step.id}
                  step={step}
                  eventId={event.id}
                  timezone={event.timezone}
                  mentionables={mentionables}
                  onMentionClick={handleMentionClick}
                  onOpenParticipants={() => setParticipantsDialogStep(step)}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <EditEventDialog event={event} open={showEditEvent} onOpenChange={setShowEditEvent} />
      <SaveAsTemplateDialog
        event={{ id: event.id, name: event.name }}
        open={showSaveAsTemplate}
        onOpenChange={setShowSaveAsTemplate}
      />
      <StepDialog event={event} step={null} open={showAddStep} onOpenChange={setShowAddStep} />
      <StepDialog
        event={event}
        step={participantsDialogStep}
        open={!!participantsDialogStep}
        onOpenChange={(o) => !o && setParticipantsDialogStep(null)}
      />

      {mention && (
        <MentionPopover
          state={mention}
          onClose={() => setMention(null)}
          onNavigateVendor={(vendorId) => {
            navigate(`/vendors/${vendorId}`);
            setMention(null);
          }}
          onNavigateProject={() => {
            navigate(`/projects/${projectId}`);
            setMention(null);
          }}
        />
      )}
    </div>
  );
}

// Portal-positioned popover. We don't use the radix `<Popover>` here because
// the anchor isn't a React element — it's a Tiptap-rendered DOM node — and
// radix's anchor model expects the trigger to be a child component.
function MentionPopover({
  state,
  onClose,
  onNavigateVendor,
  onNavigateProject,
}: {
  state: MentionPopoverState;
  onClose: () => void;
  onNavigateVendor: (vendorId: string) => void;
  onNavigateProject: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const rect = state.anchor.getBoundingClientRect();
    setPos({
      top: window.scrollY + rect.bottom + 6,
      left: window.scrollX + rect.left,
    });
  }, [state.anchor]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      if (state.anchor.contains(e.target as Node)) return;
      onClose();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose, state.anchor]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      style={{ top: pos.top, left: pos.left }}
      className="absolute z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-ambient"
    >
      {state.kind === 'vendor' && state.vendor ? (
        <div className="space-y-2">
          <div>
            <p className="font-medium text-sm">{state.vendor.name}</p>
            {state.vendor.role && (
              <p className="label-caps text-[10px] text-muted-foreground">{state.vendor.role}</p>
            )}
          </div>
          {state.vendor.contact && (
            <div className="space-y-1 pt-1 border-t">
              <p className="text-sm">{state.vendor.contact.name}</p>
              {state.vendor.contact.email && (
                <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                  <Mail className="size-3" />
                  {state.vendor.contact.email}
                </p>
              )}
              {(state.vendor.contact.phone ?? state.vendor.phone) && (
                <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                  <Phone className="size-3" />
                  {state.vendor.contact.phone ?? state.vendor.phone}
                </p>
              )}
            </div>
          )}
          {state.vendor.location && (
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <MapPin className="size-3" />
              {state.vendor.location}
            </p>
          )}
          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => onNavigateVendor(state.vendor!.vendorId)}>
              <ExternalLink className="size-3" />
              Open vendor
            </Button>
          </div>
        </div>
      ) : state.kind === 'person' && state.personKey ? (
        <div className="space-y-1">
          <p className="font-medium text-sm">{PERSON_KEY_LABELS[state.personKey]}</p>
          <p className="text-xs text-muted-foreground">
            From the project's people. Edit on the project to update name/contact.
          </p>
          <Button size="sm" variant="outline" className="mt-2" onClick={onNavigateProject}>
            <ExternalLink className="size-3" />
            Open project
          </Button>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

// ─── Summary header ──────────────────────────────────────────────────────────

function SummaryHeader({ event }: { event: EventDetail }) {
  const arrivals = event.summary?.vendorArrivals ?? [];
  const timelineRows = event.summary?.timelineRows ?? [];
  const tz = event.timezone;
  return (
    <div className="grid md:grid-cols-2 border-b">
      <div className="p-5 border-b md:border-b-0 md:border-r">
        <h3 className="label-caps text-xs text-muted-foreground mb-3">
          Vendor arrival & contact
        </h3>
        {arrivals.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            No vendor participation yet — mention a vendor on a step to populate this.
          </p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {arrivals.map((a) => (
              <li
                key={a.projectVendorId}
                className="grid grid-cols-[5rem_1fr_auto] gap-3 items-baseline"
              >
                <span className="tabular-nums text-muted-foreground">
                  {formatTimeInZone(a.arrivalAt, tz)}
                </span>
                <span className="font-medium truncate">
                  {a.name}
                  {a.contactName && (
                    <span className="text-muted-foreground"> · {a.contactName}</span>
                  )}
                </span>
                {a.phone && <span className="text-xs text-muted-foreground tabular-nums">{a.phone}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="p-5">
        <h3 className="label-caps text-xs text-muted-foreground mb-3">Timeline summary</h3>
        {timelineRows.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            All steps are excluded from the summary.
          </p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {timelineRows.map((row) => (
              <li key={row.stepId} className="grid grid-cols-[5rem_1fr] gap-3 items-baseline">
                <span className="tabular-nums text-muted-foreground">
                  {formatTimeInZone(row.time, tz)}
                </span>
                <span>{row.title}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Inline-edit primitives ──────────────────────────────────────────────────

// Centralized partial-update mutation with optimistic write to the cached
// event detail. Each cell uses this so we don't refetch the whole event for
// every keystroke.
type StepPatch = Partial<{
  title: string;
  category: string | null;
  startAt: string;
  endAt: string | null;
  bodyJson: unknown;
  bodyText: string;
  includeInSummary: boolean;
}>;

function useUpdateStep(eventId: string, stepId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: StepPatch) => {
      const res = await client.api['event-steps'][':id'].$put({
        param: { id: stepId },
        json: patch,
      });
      if (!res.ok) throw new Error('Failed to save');
      return res.json();
    },
    onMutate: async (patch) => {
      // Apply the optimistic write SYNCHRONOUSLY before any awaits — the
      // BodyCell unmounts the editor on commit and the view-mode RichBody
      // mounts fresh on the very next render, so cache must be current by
      // then or the view shows the pre-edit content.
      const prev = queryClient.getQueryData<EventDetail>(['event', eventId]);
      if (prev) {
        // Cast: cached EventDetail's bodyJson is the narrow JSONValue from
        // Hono RPC inference but the patch carries `unknown` Tiptap output.
        // Runtime shape matches.
        queryClient.setQueryData<EventDetail>(['event', eventId], {
          ...prev,
          steps: prev.steps.map((s) =>
            s.id === stepId ? ({ ...s, ...patch } as EventDetail['steps'][number]) : s,
          ),
        });
      }
      await queryClient.cancelQueries({ queryKey: ['event', eventId] });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['event', eventId], ctx.prev);
    },
    // Refetch on settle to pull derived `summary` + any auto-added participants
    // from the mention-sync the backend ran.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}

function useDeleteStep(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (stepId: string) => {
      const res = await client.api['event-steps'][':id'].$delete({ param: { id: stepId } });
      if (!res.ok) throw new Error('Failed to delete');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}

// Detect a click outside `ref` (and outside any radix portal floating UI)
// while editing — used to commit-and-exit edit mode the way Notion does.
function useExitOnOutsideClick(
  active: boolean,
  ref: React.RefObject<HTMLElement | null>,
  onExit: () => void,
) {
  useEffect(() => {
    if (!active) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (ref.current?.contains(target)) return;
      // Don't exit if the click landed in a radix popover/dropdown portal —
      // those belong to the editor's own toolbars (highlight color picker,
      // mention picker, etc.).
      if (
        target instanceof Element &&
        (target.closest('[data-radix-popper-content-wrapper]') ||
          target.closest('[role="menu"]'))
      ) {
        return;
      }
      onExit();
    };
    // Use mousedown so the commit happens before any subsequent click handler.
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [active, ref, onExit]);
}

// ─── Cells ───────────────────────────────────────────────────────────────────

function TimeCell({
  step,
  timezone,
  onSave,
}: {
  step: Step;
  timezone: string;
  onSave: (patch: StepPatch) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [hasEnd, setHasEnd] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const enter = () => {
    setStart(instantToZonedWallClock(step.startAt, timezone));
    setEnd(step.endAt ? instantToZonedWallClock(step.endAt, timezone) : '');
    setHasEnd(step.endAt !== null);
    setEditing(true);
  };

  const commit = useCallback(() => {
    try {
      const startIso = zonedWallClockToInstant(start, timezone).toISOString();
      const endIso = hasEnd && end ? zonedWallClockToInstant(end, timezone).toISOString() : null;
      const patch: StepPatch = {};
      if (startIso !== step.startAt) patch.startAt = startIso;
      if (endIso !== step.endAt) patch.endAt = endIso;
      if (Object.keys(patch).length > 0) onSave(patch);
    } catch {
      // Bad input — silently bail and keep stored value
    }
    setEditing(false);
  }, [start, end, hasEnd, timezone, step.startAt, step.endAt, onSave]);

  useExitOnOutsideClick(editing, ref, commit);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={enter}
        className="text-left rounded px-1 -mx-1 py-0.5 hover:bg-muted/40 transition-colors"
      >
        <div className="text-sm font-medium tabular-nums">
          {formatTimeInZone(step.startAt, timezone)}
        </div>
        {step.endAt && (
          <div className="text-[11px] text-muted-foreground tabular-nums">
            – {formatTimeInZone(step.endAt, timezone)}
          </div>
        )}
      </button>
    );
  }

  return (
    <div ref={ref} className="space-y-1">
      <Input
        autoFocus
        type="datetime-local"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-7 text-xs px-1.5"
      />
      {hasEnd ? (
        <div className="flex items-center gap-1">
          <Input
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="h-7 text-xs px-1.5 flex-1"
          />
          <button
            type="button"
            onClick={() => {
              setHasEnd(false);
              setEnd('');
            }}
            title="Remove end time"
            className="text-muted-foreground hover:text-foreground text-xs px-1"
          >
            ×
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setHasEnd(true)}
          className="text-[10px] text-muted-foreground hover:text-foreground"
        >
          + end time
        </button>
      )}
    </div>
  );
}

function TitleCell({
  step,
  onSave,
}: {
  step: Step;
  onSave: (patch: StepPatch) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  const enter = () => {
    setCategory(step.category ?? '');
    setTitle(step.title);
    setEditing(true);
  };

  const commit = useCallback(() => {
    const patch: StepPatch = {};
    const trimmedTitle = title.trim();
    const trimmedCategory = category.trim();
    if (trimmedTitle && trimmedTitle !== step.title) patch.title = trimmedTitle;
    if ((trimmedCategory || null) !== step.category) {
      patch.category = trimmedCategory || null;
    }
    if (Object.keys(patch).length > 0) onSave(patch);
    setEditing(false);
  }, [category, title, step.title, step.category, onSave]);

  useExitOnOutsideClick(editing, ref, commit);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={enter}
        className="text-left rounded px-1 -mx-1 py-0.5 hover:bg-muted/40 transition-colors w-full"
      >
        {step.category && (
          <div className="label-caps text-xs text-foreground/80">{step.category}</div>
        )}
        <div className="text-sm font-semibold">{step.title}</div>
      </button>
    );
  }

  return (
    <div ref={ref} className="space-y-1">
      <Input
        value={category}
        onChange={(e) => setCategory(e.target.value.toUpperCase())}
        placeholder="CATEGORY"
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-6 text-[11px] px-1.5 label-caps"
      />
      <Input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-7 text-sm font-semibold px-1.5"
      />
    </div>
  );
}

function BodyCell({
  step,
  mentionables,
  onSave,
  onMentionClick,
}: {
  step: Step;
  mentionables: Mentionable[];
  onSave: (patch: StepPatch) => void;
  onMentionClick: (id: string, kind: 'vendor' | 'person', target?: HTMLElement) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ json: unknown; text: string } | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const commit = useCallback(() => {
    if (draft) {
      const sameJson = JSON.stringify(draft.json) === JSON.stringify(step.bodyJson);
      if (!sameJson) {
        onSave({ bodyJson: draft.json, bodyText: draft.text });
      }
    }
    setDraft(null);
    setEditing(false);
  }, [draft, step.bodyJson, onSave]);

  useExitOnOutsideClick(editing, ref, commit);

  // Don't enter edit mode when the click landed on an interactive element we
  // explicitly support in view mode — currently mention chips (open popover)
  // and task-list checkboxes (toggle inline via onReadOnlyEdit).
  const handleEnter = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.mention-chip')) return;
    if (target.closest('input[type="checkbox"]') || target.closest('label')) return;
    setEditing(true);
  };

  if (!editing) {
    return (
      <div
        ref={ref}
        onClick={handleEnter}
        className="rounded px-1 -mx-1 py-0.5 hover:bg-muted/40 transition-colors min-h-[1.75rem] cursor-text"
      >
        {step.bodyJson ? (
          <RichBody
            value={step.bodyJson}
            editable={false}
            onMentionClick={onMentionClick}
            onReadOnlyEdit={(json, text) => onSave({ bodyJson: json, bodyText: text })}
          />
        ) : (
          <span className="text-xs text-muted-foreground italic">
            Click to add details — checklist, table, mention with @
          </span>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="rounded border border-input bg-background px-2 py-1.5">
      <RichBody
        key={`body-edit-${step.id}`}
        editable
        value={step.bodyJson}
        placeholder="Add details — checklist, table, mention with @"
        mentionables={mentionables}
        onChange={(json, text) => setDraft({ json, text })}
      />
    </div>
  );
}

// ─── Single step row ─────────────────────────────────────────────────────────

function StepRow({
  step,
  eventId,
  timezone,
  mentionables,
  onMentionClick,
  onOpenParticipants,
}: {
  step: Step;
  eventId: string;
  timezone: string;
  mentionables: Mentionable[];
  onMentionClick: (id: string, kind: 'vendor' | 'person', target?: HTMLElement) => void;
  onOpenParticipants: () => void;
}) {
  const updateStep = useUpdateStep(eventId, step.id);
  const deleteStep = useDeleteStep(eventId);

  const handleMention = (id: string, kind: 'vendor' | 'person') => {
    const target =
      (document.activeElement instanceof HTMLElement ? document.activeElement : null) ??
      (document.querySelector(`[data-id="${CSS.escape(id)}"]`) as HTMLElement | null);
    onMentionClick(id, kind, target ?? undefined);
  };

  return (
    <div className="event-doc-row grid grid-cols-[6rem_10rem_1fr_auto] gap-x-4 px-6 py-4 border-b last:border-b-0 group items-start">
      <TimeCell step={step} timezone={timezone} onSave={updateStep.mutate} />
      <TitleCell step={step} onSave={updateStep.mutate} />
      <BodyCell
        step={step}
        mentionables={mentionables}
        onSave={updateStep.mutate}
        onMentionClick={handleMention}
      />
      <div
        className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity no-print pt-1"
        data-no-print
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-7">
              <MoreHorizontal className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[12rem]">
            <DropdownMenuItem
              onClick={() =>
                updateStep.mutate({ includeInSummary: !step.includeInSummary })
              }
            >
              {step.includeInSummary ? (
                <>
                  <EyeOff className="size-3.5" /> Hide from summary
                </>
              ) : (
                <>
                  <Eye className="size-3.5" /> Show in summary
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenParticipants}>
              <Users className="size-3.5" /> Manage participants…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => {
                if (confirm('Delete this step?')) deleteStep.mutate(step.id);
              }}
            >
              <Trash2 className="size-3.5" /> Delete step
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
